import React, { useContext, useEffect, useState } from "react";
import { Title } from "../../../../../stories/Title/Title";
import { Button } from "../../../../../stories/Button/Button";
import { useNavigate } from "react-router-dom";
import {
  LicenseTypes,
  MasterPages,
  Operation,
  PageFor,
} from "../../../../../utils/Enum.types";
import Add from "./Add";
import List from "./List";
import { IObjectKeys } from "../../../../../utils/formTypes";
import { Form, Formik, FormikProps } from "formik";
import { categoryValidation } from "../../../../../utils/validationSchema";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  EMPTY_STRING,
  emptyMessage,
  ROWS_PER_PAGE,
} from "../../../../../utils/constants";
import useUrlParams from "../../../../../customHooks/useUrlParams";

import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import { nodeVar, OrderDirection } from "../../../../../utils/commonQueryTypes";
import { AppContext } from "../../../../../context/context";
import MessageModal from "../../../../../customComponents/MessageModal";
import { GetGroupNameByNodeId, GetStockGroups } from "./queries/queries";
import {
  AddStockGroupResult,
  AddStockGroupVar,
  AtsStckGroup,
  AtsStckGroupOrderField,
  UpdateStockGroupResult,
  UpdateStockGroupVar,
} from "./queries/types";
import { AddStockGroup, UpdateStockGroup } from "./queries/mutations";
import Close from "../../../../../images/Close.svg";
import LoadingModal from "../../../../pages/LoadingModal";
import { payloadTypes } from "../../../../../context/reducer";
import Navbar from "../../../../common/layout/Navbar";
import useLabels from "../../../../../customHooks/useLabels";

interface formDataType extends IObjectKeys {
  groupName: string;
}

export interface MasterDataProps {
  type: PageFor;
  setModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface ListProps extends MasterDataProps {
  setOperation: React.Dispatch<React.SetStateAction<Operation>>;
}
export interface AddMasterDataProps extends ListProps {
  formData: formDataType;
  handleValueChange: (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    meta: FormikProps<formDataType>
  ) => void;
  meta: FormikProps<formDataType>;
  operation: Operation;
}

const ItemGroup = ({ type, setModal }: MasterDataProps) => {
  const navigate = useNavigate();
  const [message, setMessage] = useState(emptyMessage);

  const { itemLabel } = useLabels();
  const { user_details } = useLoggedInUserDetails();

  const { state, dispatch } = useContext(AppContext);

  const [operation, setOperation] = useState(Operation.CREATE);
  const { customer_id } = useUrlParams();
  const [formData, setFormData] = useState<formDataType>({
    groupName: "",
  });

  const [GetGroupByNodeId, { loading: GetGroupNameByNodeIdLoading }] =
    useLazyQuery<{ node: AtsStckGroup }, nodeVar>(GetGroupNameByNodeId);

  const [AddCategoryDetails, { loading: AddStockGroupLoading }] = useMutation<
    AddStockGroupResult,
    AddStockGroupVar
  >(AddStockGroup, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });
  const [UpdateGroup, { loading: UpdateStockGroupLoading }] = useMutation<
    UpdateStockGroupResult,
    UpdateStockGroupVar
  >(UpdateStockGroup, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const handleValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    meta: FormikProps<formDataType>
  ) => {
    const { name, value } = e.target as HTMLInputElement;

    setFormData(() => ({
      ...formData,
      [name]: value,
    }));
    meta.handleChange(e);
  };
  const handleClear = () => {
    const inputElement = document.getElementsByName(
      "groupName"
    )[0] as HTMLInputElement;
    if (inputElement) inputElement.focus();
    setFormData({
      groupName: "",
    });
  };

  const handleSubmit = async () => {
    try {
      if (customer_id) {
        if (operation === Operation.CREATE) {
          const empAdded = AddCategoryDetails({
            variables: {
              user_details,
              customer_id,
              group_name: formData.groupName,
            },
            refetchQueries: [
              {
                query: GetStockGroups,
                variables: {
                  after: null,
                  customerID: customer_id,
                  first: ROWS_PER_PAGE,
                  orderBy: {
                    direction: OrderDirection.ASC,
                    field: AtsStckGroupOrderField.GROUP_NAME,
                  },

                  name: EMPTY_STRING,
                },
              },
            ],
          });
          const { data } = await empAdded;

          if (data && data.AddStockGroup) {
            setMessage({
              flag: true,
              message: "Group added successfully",
              operation: Operation.CREATE,
            });
          }
        } else {
          const customerUpdated = UpdateGroup({
            variables: {
              user_details,
              group_id: state.itmGroupId,
              group_name: formData.groupName,
            },
            refetchQueries: [
              {
                query: GetStockGroups,
                variables: {
                  after: null,
                  customerID: customer_id,
                  first: ROWS_PER_PAGE,
                  orderBy: {
                    direction: OrderDirection.ASC,
                    field: AtsStckGroupOrderField.GROUP_NAME,
                  },

                  name: EMPTY_STRING,
                },
              },
            ],
          });
          const { data } = await customerUpdated;

          if (data && data.UpdateStockGroup) {
            setMessage({
              flag: true,
              message: "Group details updated successfully",
              operation: Operation.CREATE,
            });
            dispatch({
              type: payloadTypes.SET_ITM_GROUP_ID,
              payload: {
                itmGroupId: 0,
              },
            });
          }
        }
      }
    } catch (err) {
      setMessage({
        flag: true,
        message: (err as Error).message,
        operation: Operation.NONE,
      });
    }
  };

  const hanldeClear = () => {
    setFormData({
      groupName: "",
    });
  };
  const handleClose = () => {
    if (message.operation !== Operation.NONE) {
      hanldeClear();
      setOperation(Operation.CREATE);
    }
    setMessage(emptyMessage);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (state.itmGroupId && operation === Operation.UPDATE) {
          GetGroupByNodeId({
            variables: {
              id: state.itmGroupId,
            },
          }).then(({ data }) => {
            if (data && data.node) {
              setFormData({
                groupName: data.node.groupName,
              });
            }
          });
        }
      } catch (err) {
        setMessage({
          flag: true,
          message: (err as Error).message,
          operation: Operation.NONE,
        });
      }
    };

    fetchData();
  }, [state.itmGroupId, GetGroupByNodeId, operation]);

  return (
    <>
      {type === PageFor.GENERAL && (
        <Navbar NavType={LicenseTypes.CUSTOMER_USERS} />
      )}
      <div className="master-data--modal--title">
        <Title>{itemLabel} Group</Title>
        {type === PageFor.MODAL && (
          <img src={Close} alt="" onClick={() => setModal?.(false)} />
        )}
      </div>
      <Formik
        initialValues={formData}
        validationSchema={categoryValidation}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange
      >
        {(meta) => {
          return (
            <Form
              className={`master-data${
                type === PageFor.MODAL ? "--modal" : ""
              }`}
            >
              <div
                className={`master-data__datablock${
                  type === PageFor.MODAL ? "--modal" : ""
                }`}
              >
                <div className="master-data__datablock--left">
                  <Add
                    type={PageFor.GENERAL}
                    formData={formData}
                    handleValueChange={handleValueChange}
                    setOperation={setOperation}
                    meta={meta}
                    operation={operation}
                  />
                </div>
                <div className="master-data__datablock--right">
                  <List type={PageFor.GENERAL} setOperation={setOperation} />
                </div>
              </div>
              <Button mode="save" className="sub" type="submit" />
              <Button mode="clear" type="button" onClick={handleClear} />
              {type === PageFor.GENERAL ? (
                <Button
                  mode="back"
                  type="button"
                  onClick={() => navigate(-1)}
                />
              ) : (
                <Button mode="cancel" onClick={() => setModal?.(false)} />
              )}
            </Form>
          );
        }}
      </Formik>
      <MessageModal
        handleClose={handleClose}
        modalFlag={message.flag}
        operation={message.operation}
        value={message.message}
      />
      <LoadingModal
        flag={
          GetGroupNameByNodeIdLoading ||
          AddStockGroupLoading ||
          UpdateStockGroupLoading
        }
      />
    </>
  );
};

export default ItemGroup;
